import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { checkIfInvalidName, getFullName } from 'Util/StringUtils';
import { appConfigStore } from 'Models/AppConfigStore';
import { isNotNullOrUndefined, stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { isEmail } from 'Validators/Functions/Email';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { validatePassword } from 'Validators/Functions/HumanWritten/Password';
import { IGroupResult, store } from '../Store';

export interface IUserEntityAttributes extends IModelAttributes {
	email: string;
	userName: string;
	twoFactorEnabled: boolean;
	firstName: string;
	lastName: string;
	phone: string;
	postcode: string;
	additionalInformation?: string;
	legacyId: string;
	newEmailAddress: string;
	needsPasswordReset: boolean;
	disabled: boolean;
	paymentProviderCustomerId: string;
	emailConfirmed: boolean;
	lastSeenVersion: string;
	isGuest?: boolean;

	addOnsPrices?: Array<Models.AddOnsPriceEntity | Models.IAddOnsPriceEntityAttributes>;
	bookings: Array<Models.BookingEntity | Models.IBookingEntityAttributes>;
	cargos: Array<Models.CargoEntity | Models.ICargoEntityAttributes>;
	freightContacts: Array<Models.FreightContactEntity | Models.IFreightContactEntityAttributes>;
	giftCertificates: Array<Models.GiftCertificateEntity | Models.IGiftCertificateEntityAttributes>;
	noteAboutUsers: Array<Models.NotesEntity | Models.INotesEntityAttributes>;
	notes: Array<Models.UserNote | Models.IUserNoteAttributes>;
	refunds: Array<Models.RefundEntity | Models.IRefundEntityAttributes>;
	checkInStatusHistories: Array<Models.CheckInStatusHistoryEntity | Models.ICheckInStatusHistoryEntityAttributes>;
	passengers: Array<Models.PassengerEntity | Models.IPassengerEntityAttributes>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	userDisplayNameId?: string;
	userDisplayName?: Models.UserDisplayNameEntity | Models.IUserDisplayNameEntityAttributes;
	waitlistItems: Array<Models.WaitlistItemEntity | Models.IWaitlistItemEntityAttributes>;
	pricingStrategyProfileId?: string;
	pricingStrategyProfile?: Models.PricingStrategyProfileEntity | Models.IPricingStrategyProfileEntityAttributes;
	twoFactorSetupRequired: boolean;
	/**
	 * This is a custom GraphQL resolver (see UserEntityType.cs).
	 *
	 * Returns the user's top three vehicles used in their latest alterations (unique by rego).
	 */
	latestVehicles: Array<Models.CargoEntity | Models.ICargoEntityAttributes>;
	companyName?: string;
	userGroups?: {
		roleId?: string;
		role?: IGroupResult;
	}[];
	isSubscribedToMarketing?: boolean;
	lastLoginAt?: Date;
	userEntitlements: Array<Models.UserEntitlementEntity | Models.IUserEntitlementEntityAttributes>;
}
@entity('UserEntity', 'User')
export default class UserEntity extends Model implements IUserEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = ['email', 'userName', 'twoFactorEnabled'];

	@observable
	@attribute()
	public email: string;

	@observable
	@attribute()
	public twoFactorEnabled: boolean;

	@observable
	@attribute()
	public userName: string;

	@observable
	public password: string;

	@observable
	public _confirmPassword: string;

	@observable
	@attribute()
	public firstName: string;

	@observable
	@attribute()
	public lastName: string;

	// @Validators.Required(undefined, appConfigStore.userPhoneEnabled && appConfigStore.userPhoneRequired)
	// @Validators.Phone(!appConfigStore.userPhoneEnabled || !appConfigStore.userPhoneRequired) // Must be a valid phone number (from any country)
	@observable
	@attribute<UserEntity, string>()
	public phone: string;

	/**
	 * The postcode that the user resides in
	 */
	// @Validators.Required(model => {
	// 	return !appConfigStore.userPostcodeEnabled || !stringIsEmpty((model as UserEntity).postcode);
	// }, appConfigStore.userPostcodeEnabled)
	@observable
	@attribute()
	public postcode: string;

	/**
	 * The address of the user.
	 */
	@observable
	@attribute<UserEntity, string>()
	public additionalInformation?: string;

	@observable
	@attribute<UserEntity, string>()
	public companyName?: string;

	@observable
	@attribute<UserEntity, number>()
	public legacyId: string;

	/**
	 * Email address used for when a user tries to change their account email address.
	 */
	@observable
	@attribute<UserEntity, string>()
	public newEmailAddress: string;

	/**
	 * Initially true for migrated users
	 */
	@observable
	@attribute<UserEntity, boolean>()
	public needsPasswordReset: boolean;

	@observable
	@attribute<UserEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<UserEntity, string>()
	public paymentProviderCustomerId: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public addOnsPrices: Models.AddOnsPriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public bookings: Models.BookingEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargos: Models.CargoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightContacts: Models.FreightContactEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public giftCertificates: Models.GiftCertificateEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public noteAboutUsers: Models.NotesEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public notes: Models.UserNote[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public checkInStatusHistories: Models.CheckInStatusHistoryEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengers: Models.PassengerEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	@observable
	@attribute()
	public userDisplayNameId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public userDisplayName?: Models.UserDisplayNameEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public waitlistItems: Models.WaitlistItemEntity[] = [];

	@observable
	@attribute()
	public pricingStrategyProfileId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public pricingStrategyProfile: Models.PricingStrategyProfileEntity;

	@observable
	@attribute<UserEntity, boolean>()
	public twoFactorSetupRequired: boolean;

	// See interface for latestVehicles definition
	@observable
	public latestVehicles: Models.CargoEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public refunds: Models.RefundEntity[] = [];

	@observable
	@attribute<UserEntity, string>()
	public lastSeenVersion: string;

	@observable
	@attribute<UserEntity, string>()
	public isGuest: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public userEntitlements: Models.UserEntitlementEntity[] = [];

	@observable
	public userGroups: {
		roleId?: string;
		role?: IGroupResult;
	}[] = [];

	@observable
	public emailConfirmed: boolean;

	/**
	 * Whether the user is subscribed to marketing communications.
	 */
	@observable
	@attribute<UserEntity, boolean>()
	public isSubscribedToMarketing?: boolean;

	/**
	 * The timestamp of the user's last login.
	 */
	@observable
	@attribute<UserEntity, Date>()
	public lastLoginAt?: Date;

	constructor(attributes?: Partial<IUserEntityAttributes>) {
		super(attributes);

		this.addOnsPrices = [];
		this.bookings = [];
		this.cargos = [];
		this.freightContacts = [];
		this.giftCertificates = [];
		this.noteAboutUsers = [];
		this.notes = [];
		this.refunds = [];
		this.checkInStatusHistories = [];
		this.passengers = [];
		this.prices = [];
		this.waitlistItems = [];
		this.latestVehicles = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IUserEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.email !== undefined) {
				this.email = attributes.email;
			}
			if (attributes.userName !== undefined) {
				this.userName = attributes.userName;
			}
			if (attributes.twoFactorEnabled !== undefined) {
				this.twoFactorEnabled = attributes.twoFactorEnabled;
			}
			if (attributes.firstName !== undefined) {
				this.firstName = attributes.firstName;
			}
			if (attributes.lastName !== undefined) {
				this.lastName = attributes.lastName;
			}
			if (attributes.phone !== undefined) {
				this.phone = attributes.phone;
			}
			if (attributes.postcode !== undefined) {
				this.postcode = attributes.postcode;
			}
			if (attributes.additionalInformation !== undefined) {
				this.additionalInformation = attributes.additionalInformation;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.newEmailAddress !== undefined) {
				this.newEmailAddress = attributes.newEmailAddress;
			}
			if (attributes.needsPasswordReset !== undefined) {
				this.needsPasswordReset = attributes.needsPasswordReset;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.paymentProviderCustomerId !== undefined) {
				this.paymentProviderCustomerId = attributes.paymentProviderCustomerId;
			}
			if (attributes.lastSeenVersion !== undefined) {
				this.lastSeenVersion = attributes.lastSeenVersion;
			}
			if (attributes.isGuest !== undefined) {
				this.isGuest = attributes.isGuest;
			}
			if (attributes.addOnsPrices !== undefined && Array.isArray(attributes.addOnsPrices)) {
				if (!this.addOnsPrices) {
					this.addOnsPrices = [];
				}
				for (const model of attributes.addOnsPrices) {
					if (model instanceof Models.AddOnsPriceEntity) {
						this.addOnsPrices.push(model);
					} else {
						this.addOnsPrices.push(new Models.AddOnsPriceEntity(model));
					}
				}
			}
			if (attributes.bookings !== undefined && Array.isArray(attributes.bookings)) {
				if (!this.bookings) {
					this.bookings = [];
				}
				for (const model of attributes.bookings) {
					if (model instanceof Models.BookingEntity) {
						this.bookings.push(model);
					} else {
						this.bookings.push(new Models.BookingEntity(model));
					}
				}
			}
			if (attributes.cargos !== undefined && Array.isArray(attributes.cargos)) {
				if (!this.cargos) {
					this.cargos = [];
				}
				for (const model of attributes.cargos) {
					if (model instanceof Models.CargoEntity) {
						this.cargos.push(model);
					} else {
						this.cargos.push(new Models.CargoEntity(model));
					}
				}
			}
			if (attributes.checkInStatusHistories !== undefined && Array.isArray(attributes.checkInStatusHistories)) {
				if (!this.checkInStatusHistories) {
					this.checkInStatusHistories = [];
				}
				for (const model of attributes.checkInStatusHistories) {
					if (model instanceof Models.CheckInStatusHistoryEntity) {
						this.checkInStatusHistories.push(model);
					} else {
						this.checkInStatusHistories.push(new Models.CheckInStatusHistoryEntity(model));
					}
				}
			}
			if (attributes.freightContacts !== undefined && Array.isArray(attributes.freightContacts)) {
				if (!this.freightContacts) {
					this.freightContacts = [];
				}
				for (const model of attributes.freightContacts) {
					if (model instanceof Models.FreightContactEntity) {
						this.freightContacts.push(model);
					} else {
						this.freightContacts.push(new Models.FreightContactEntity(model));
					}
				}
			}
			if (attributes.giftCertificates !== undefined && Array.isArray(attributes.giftCertificates)) {
				if (!this.giftCertificates) {
					this.giftCertificates = [];
				}
				for (const model of attributes.giftCertificates) {
					if (model instanceof Models.GiftCertificateEntity) {
						this.giftCertificates.push(model);
					} else {
						this.giftCertificates.push(new Models.GiftCertificateEntity(model));
					}
				}
			}
			if (attributes.noteAboutUsers !== undefined && Array.isArray(attributes.noteAboutUsers)) {
				if (!this.noteAboutUsers) {
					this.noteAboutUsers = [];
				}
				for (const model of attributes.noteAboutUsers) {
					if (model instanceof Models.NotesEntity) {
						this.noteAboutUsers.push(model);
					} else {
						this.noteAboutUsers.push(new Models.NotesEntity(model));
					}
				}
			}
			if (attributes.notes !== undefined && Array.isArray(attributes.notes)) {
				if (!this.notes) {
					this.notes = [];
				}
				for (const model of attributes.notes) {
					if (model instanceof Models.UserNote) {
						this.notes.push(model);
					} else {
						this.notes.push(new Models.UserNote(model));
					}
				}
			}
			if (attributes.passengers !== undefined && Array.isArray(attributes.passengers)) {
				if (!this.passengers) {
					this.passengers = [];
				}
				for (const model of attributes.passengers) {
					if (model instanceof Models.PassengerEntity) {
						this.passengers.push(model);
					} else {
						this.passengers.push(new Models.PassengerEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.userDisplayNameId !== undefined) {
				this.userDisplayNameId = attributes.userDisplayNameId;
			}
			if (attributes.userDisplayName !== undefined) {
				if (attributes.userDisplayName === null) {
					this.userDisplayName = attributes.userDisplayName;
				} else if (attributes.userDisplayName instanceof Models.UserDisplayNameEntity) {
					this.userDisplayName = attributes.userDisplayName;
					this.userDisplayNameId = attributes.userDisplayName.id;
				} else {
					this.userDisplayName = new Models.UserDisplayNameEntity(attributes.userDisplayName);
					this.userDisplayNameId = this.userDisplayName.id;
				}
			}
			if (attributes.waitlistItems !== undefined && Array.isArray(attributes.waitlistItems)) {
				if (!this.waitlistItems) {
					this.waitlistItems = [];
				}
				for (const model of attributes.waitlistItems) {
					if (model instanceof Models.WaitlistItemEntity) {
						this.waitlistItems.push(model);
					} else {
						this.waitlistItems.push(new Models.WaitlistItemEntity(model));
					}
				}
			}
			if (attributes.pricingStrategyProfileId !== undefined) {
				this.pricingStrategyProfileId = attributes.pricingStrategyProfileId;
			}
			if (attributes.pricingStrategyProfile !== undefined) {
				if (attributes.pricingStrategyProfile === null) {
					this.pricingStrategyProfile = attributes.pricingStrategyProfile;
				} else if (attributes.pricingStrategyProfile instanceof Models.PricingStrategyProfileEntity) {
					this.pricingStrategyProfile = attributes.pricingStrategyProfile;
					this.pricingStrategyProfileId = attributes.pricingStrategyProfile.id;
				} else {
					this.pricingStrategyProfile = new Models.PricingStrategyProfileEntity(
						attributes.pricingStrategyProfile,
					);
					this.pricingStrategyProfileId = this.pricingStrategyProfile.id;
				}
			}
			if (attributes.refunds !== undefined && Array.isArray(attributes.refunds)) {
				if (!this.refunds) {
					this.refunds = [];
				}
				for (const model of attributes.refunds) {
					if (model instanceof Models.RefundEntity) {
						this.refunds.push(model);
					} else {
						this.refunds.push(new Models.RefundEntity(model));
					}
				}
			}
			if (attributes.latestVehicles !== undefined && Array.isArray(attributes.latestVehicles)) {
				if (!this.latestVehicles) {
					this.latestVehicles = [];
				}
				for (const model of attributes.latestVehicles) {
					if (model instanceof Models.CargoEntity) {
						this.latestVehicles.push(model);
					} else {
						this.latestVehicles.push(new Models.CargoEntity(model));
					}
				}
			}
			if (attributes.twoFactorSetupRequired !== undefined) {
				this.twoFactorSetupRequired = attributes.twoFactorSetupRequired;
			}
			if (isNotNullOrUndefined(attributes?.companyName)) {
				this.companyName = attributes?.companyName;
			}
			if (attributes?.userGroups !== undefined && Array.isArray(attributes.userGroups)) {
				if (!this.userGroups) {
					this.userGroups = [];
				}
				for (const model of attributes.userGroups) {
					this.userGroups.push(model);
				}
			}
			if (isNotNullOrUndefined(attributes?.emailConfirmed)) {
				this.emailConfirmed = attributes?.emailConfirmed;
			}
			if (isNotNullOrUndefined(attributes?.isSubscribedToMarketing)) {
				this.isSubscribedToMarketing = attributes?.isSubscribedToMarketing;
			}
			if (isNotNullOrUndefined(attributes?.lastLoginAt)) {
				this.lastLoginAt = attributes?.lastLoginAt;
			}
		}
	}

	public getFullName(showLastNameFirst = false) {
		return getFullName(this.firstName, this.lastName, showLastNameFirst);
	}

	public hasPassengerPricing() {
		return this.prices
			.filter(x => isNotNullOrUndefined(x.passengerTypeId))
			.some(x => isNotNullOrUndefined(x.amount));
	}

	public hasVehiclePricing() {
		return this.prices.filter(x => isNotNullOrUndefined(x.measurementId)).some(x => isNotNullOrUndefined(x.amount));
	}

	public hasTrailerPricing() {
		return this.prices
			.filter(x => isNotNullOrUndefined(x.trailerMeasurementId))
			.some(x => isNotNullOrUndefined(x.amount));
	}

	public hasFreightCategoryPricing() {
		return this.prices
			.filter(x => isNotNullOrUndefined(x.freightPriceRange?.freightCategoryId))
			.some(x => isNotNullOrUndefined(x.amount));
	}

	public hasFreightHandlingPricing() {
		return this.prices
			.filter(x => isNotNullOrUndefined(x.freightPriceRange?.freightHandlingId))
			.some(x => isNotNullOrUndefined(x.amount));
	}

	public hasAddOnPricing() {
		return this.addOnsPrices.some(x => isNotNullOrUndefined(x.price));
	}

	public hasAnyPricing() {
		return (
			this.hasVehiclePricing() ||
			this.hasPassengerPricing() ||
			this.hasAddOnPricing() ||
			this.hasFreightCategoryPricing() ||
			this.hasFreightHandlingPricing()
		);
	}

	public validateRegistration(options?: {
		requirePassword?: boolean;
		isTeamMember?: boolean;
		isCompany?: boolean;
	}): Record<string, string> {
		const { requirePassword = false, isTeamMember = false, isCompany = false } = options ?? {};
		const errors: Record<string, string> = {};

		// Company name — only when the registrant opted into registering as a company, which the
		// forms only offer while the config is on.
		if (isCompany && appConfigStore.userCompanyNameEnabled && stringIsEmpty(this.companyName)) {
			errors.companyName = 'This field is required';
		}

		// Email — required and must be a valid email address.
		if (stringIsEmpty(this.email)) {
			errors.email = 'This field is required';
		} else if (!isEmail(this.email)) {
			errors.email = 'Must be a valid email address';
		}

		// Password — required and must satisfy the configured password rules. A supplied confirm
		// password must match.
		if (requirePassword) {
			if (stringIsEmpty(this.password)) {
				errors.password = 'This field is required';
			} else {
				const passwordError = validatePassword(store.passwordConfiguration, this.password);
				if (stringNotEmpty(passwordError)) {
					errors.password = passwordError;
				}
			}

			if (stringNotEmpty(this._confirmPassword) && this._confirmPassword !== this.password) {
				errors.confirmPassword = 'Password and confirm password do not match';
			}
		}

		// First / last name — required, and must contain only letters and hyphens (no spaces or
		// special characters), matching the name validation used elsewhere in the app.
		if (stringIsEmpty(this.firstName)) {
			errors.firstName = 'This field is required';
		} else if (checkIfInvalidName(this.firstName)) {
			errors.firstName = "Enter a valid first name (a-z, '-', no spaces)";
		}
		if (stringIsEmpty(this.lastName)) {
			errors.lastName = 'This field is required';
		} else if (checkIfInvalidName(this.lastName)) {
			errors.lastName = "Enter a valid last name (a-z, '-', no spaces)";
		}

		// Phone — required only when enabled and configured as required; any supplied value must be valid.
		const phoneRequired = !isTeamMember && appConfigStore.userPhoneEnabled && appConfigStore.userPhoneRequired;
		if (phoneRequired && stringIsEmpty(this.phone)) {
			errors.phone = 'This field is required';
		} else if (stringNotEmpty(this.phone) && !IsValidPhone(this.phone, false)) {
			errors.phone = 'Must be a valid phone number';
		}

		// Postcode — when enabled, any supplied value must be an integer.
		const postcodeRequired = !isTeamMember && appConfigStore.userPostcodeEnabled;
		if (postcodeRequired && stringIsEmpty(this.postcode)) {
			errors.postcode = 'This field is required';
		} else if (stringNotEmpty(this.postcode) && !/^\d+$/.test(this.postcode)) {
			errors.postcode = 'Must be a valid postcode';
		}

		return errors;
	}
}
